import React, { useState, useMemo } from "react";

import {
	Box,
	createListCollection,
	Field,
	Icon,
	MetreaSelectContent,
	MetreaSelectItem,
	MetreaSelectRoot,
	MetreaSelectTrigger,
	MetreaSelectValueText,
	Portal,
	MetreaTooltip,
} from "@metrea-ui/v2";
import classes from "./NewOption.module.css";
import { EyeOffRegular } from "@fluentui/react-icons";

import { debugLog } from "src/utils/debugUtils";

const getSelectLabel = (item) =>
	item?.label ??
	item?.extended_name ??
	item?.display_name ??
	item?.name ??
	item?.comp_value ??
	(item?.code && item?.symbol ? `${item.code} - ${item.symbol}` : undefined) ??
	item?.code ??
	item?.title ??
	item?.text ??
	String(item?.id ?? "");

const sizeToSelectSize = (size) => {
	if (size === "large") return "md";
	if (size === "small") return "sm";
	return size || "sm";
};

const buildSelectCollectionItems = (items = []) => [
	{ label: "Select...", value: "" },
	...items.map((item) => ({
		label: getSelectLabel(item),
		value: String(item?.id ?? item?.value ?? ""),
	})),
];

const renderSelectValueLabel = (selectedItems) => getSelectLabel(selectedItems?.[0]);

const NewOption = (props) => {
	const [dataError, setDataError] = useState("");

	// Always derive the available list from props
	const availableItems = useMemo(() => {
		const selectedSet = new Set((props.selected || []).map((v) => String(v)));
		return (props.item || []).filter((x) => !selectedSet.has(String(x.id)));
	}, [props.item, props.selected]);
	const availableCollection = useMemo(
		() => createListCollection({ items: buildSelectCollectionItems(availableItems) }),
		[availableItems],
	);
	// Align the hide icon to the select control instead of the helper-text row.
	const hideIconOffset = availableItems.length === 0 ? "34px" : "10px";

	const changeValueHandler = (selected) => {
		setDataError(selected !== "" ? "" : "Please choose a valid option.");

		const thisItem = availableItems.find((x) => String(x.id) === String(selected)) || null;

		debugLog(3, "[NewOption][ACTION] changeValueHandler", {
			selected: selected,
			thisItem: thisItem,
			availabeItems: availableItems,
		});

		if (!thisItem) {
			setDataError("Please choose a valid option.");
			return;
		}

		// Pass the resolved item object upward so the caller does not have to
		// re-resolve mixed string/number ids from the native select event value.
		props.onAddNew(thisItem);
		// reset to blank after adding
	};

	const cancelHandler = () => {
		props.onCancel();
	};

	const dataProps = dataError ? { error: true, helperText: dataError } : {};

	return (
		<div className={`${classes.card} ${props.className}`}>
			<Box width="100%" display="flex" gap={2} alignItems="flex-start">
				<Box flex="1 1 auto" minWidth={0}>
					<Field.Root required invalid={Boolean(dataProps.error)} style={{ width: "100%" }}>
						{availableItems.length === 0 ? (
							<Field.Label>You&apos;d used all available options.</Field.Label>
						) : null}
						<MetreaSelectRoot
							name="data"
							collection={availableCollection}
							size={sizeToSelectSize(props.size)}
							width="100%"
							variant="outline"
							invalid={Boolean(dataProps.error)}
							value={[]}
							disabled={availableItems.length === 0 || props.mode !== "edit"}
							onValueChange={(details) => {
								// Resolve the direct Select value back to the same option object shape
								// the caller already expects from the old NativeSelect path.
								const raw = Array.isArray(details?.value)
									? String(details.value[0] ?? "")
									: "";
								changeValueHandler(raw);
							}}
						>
							<MetreaSelectTrigger
								id="data"
								aria-invalid={dataProps.error ? "true" : undefined}
								aria-describedby="data-helptext"
							>
								<MetreaSelectValueText placeholder="Select...">
									{renderSelectValueLabel}
								</MetreaSelectValueText>
							</MetreaSelectTrigger>
							<Portal>
								<MetreaSelectContent>
									{availableCollection.items.map((item) => (
										<MetreaSelectItem key={item.value} item={item}>
											{item.label}
										</MetreaSelectItem>
									))}
								</MetreaSelectContent>
							</Portal>
						</MetreaSelectRoot>
						<Field.HelperText id="data-helptext">
							<span
								style={{
									display: "block",
									minHeight: "1.5em",
									fontSize: "0.75rem",
									color: dataProps.error
										? "var(--metrea-colors-border-danger, #F04438)"
										: "inherit",
									visibility: dataProps.helperText ? "visible" : "hidden",
								}}
							>
								{dataProps.helperText || "\u00A0"}
							</span>
						</Field.HelperText>
					</Field.Root>
				</Box>

				<MetreaTooltip content="Hide dropdown" positioning={{ placement: "right" }}>
					<Box as="span" display="inline-flex" mt={hideIconOffset}>
						<Icon as={EyeOffRegular} boxSize="24px" onClick={cancelHandler} />
					</Box>
				</MetreaTooltip>
			</Box>
		</div>
	);
};

export default NewOption;
